import React, { useCallback, useMemo, useRef, useState } from "react";

type DropzoneFile = {
  file: File;
  id: string;
};

export type FileDropzoneProps = {
  /** Allow multiple files (default: true) */
  multiple?: boolean;
  /** Accepted mime types/extensions, e.g. "image/*,.pdf" (passed to <input accept>) */
  accept?: string;
  /** Max file size in bytes (optional) */
  maxSizeBytes?: number;
  /** Called with accepted files */
  onFiles: (files: File[]) => void;
  /** Optional: called with rejected files and reasons */
  onReject?: (rejected: Array<{ file: File; reason: string }>) => void;
  /** Optional UI text */
  title?: string;
  subtitle?: string;
  /** Optional: disable interaction */
  disabled?: boolean;
  /** Optional: show selected file list (default: true) */
  showList?: boolean;
  /** Optional: className passthrough */
  className?: string;
};

function bytesToHuman(bytes: number) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fileId(f: File) {
  return `${f.name}:${f.size}:${f.lastModified}:${f.type}`;
}

function isAcceptedByAcceptAttr(file: File, accept?: string) {
  if (!accept?.trim()) return true;

  const rules = accept
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();

  return rules.some((rule) => {
    const r = rule.toLowerCase();
    if (r.startsWith(".")) return name.endsWith(r);
    if (r.endsWith("/*")) {
      const base = r.slice(0, r.length - 2);
      return type.startsWith(`${base}/`);
    }
    // exact mime
    return type === r;
  });
}

export default function FileDropzone({
  multiple = true,
  accept,
  maxSizeBytes,
  onFiles,
  onReject,
  title = "Drop files here",
  subtitle = "or click to browse",
  disabled = false,
  showList = true,
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [picked, setPicked] = useState<DropzoneFile[]>([]);

  const helperText = useMemo(() => {
    const parts: string[] = [];
    if (accept) parts.push(`Accepted: ${accept}`);
    if (maxSizeBytes) parts.push(`Max size: ${bytesToHuman(maxSizeBytes)}`);
    return parts.join(" • ");
  }, [accept, maxSizeBytes]);

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const validate = useCallback(
    (files: File[]) => {
      const accepted: File[] = [];
      const rejected: Array<{ file: File; reason: string }> = [];

      for (const f of files) {
        if (accept && !isAcceptedByAcceptAttr(f, accept)) {
          rejected.push({ file: f, reason: "File type not accepted" });
          continue;
        }
        if (maxSizeBytes && f.size > maxSizeBytes) {
          rejected.push({
            file: f,
            reason: `File too large (>${bytesToHuman(maxSizeBytes)})`,
          });
          continue;
        }
        accepted.push(f);
      }

      return { accepted, rejected };
    },
    [accept, maxSizeBytes]
  );

  const applyFiles = useCallback(
    (files: File[]) => {
      if (!files.length) return;

      const { accepted, rejected } = validate(files);

      if (rejected.length && onReject) onReject(rejected);

      const finalAccepted = multiple ? accepted : accepted.slice(0, 1);
      if (!finalAccepted.length) return;

      // maintain a local list for display
      setPicked((prev) => {
        const next = multiple ? [...prev] : [];
        for (const f of finalAccepted) {
          const id = fileId(f);
          if (!next.some((x) => x.id === id)) next.push({ file: f, id });
        }
        return next;
      });

      onFiles(finalAccepted);
    },
    [multiple, onFiles, onReject, validate]
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      applyFiles(files);

      // allow picking the same file again
      e.target.value = "";
    },
    [applyFiles]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files ?? []);
      applyFiles(files);
    },
    [applyFiles, disabled]
  );

  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      // Indicate we're a valid drop target
      e.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    },
    [disabled]
  );

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const removeFile = useCallback((id: string) => {
    setPicked((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={multiple}
        accept={accept}
        onChange={onInputChange}
        disabled={disabled}
      />

      <div
        className="dropzone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") openPicker();
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <div style={{ opacity: 0.75 }}>{subtitle}</div>
          {helperText && (
            <div style={{ opacity: 0.65, fontSize: 12 }}>{helperText}</div>
          )}
        </div>
      </div>
    </div>
  );
}
